import React, { useMemo, useState } from 'react';

import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DataTableColumnsSelector } from '@/components/data-table/data-table-columns-selector';
import { DataTableFiltersV2, useTableFilters } from '@/components/data-table/filters-v2';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useDataTable } from '@/hooks/use-data-table';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { TableIds } from '@/lib/constants/table-ids';
import { PageTemplate, usePermissions } from '@/modules/common';
import { ApiError } from '@/modules/common/api/errors';
import { RefreshData } from '@/modules/common/components/organisms/refresh-data';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { FcmNotificationType } from '@/modules/notifications';
import { useFcmListRefresh } from '@/modules/notifications/hooks';

import { DeleteProjectsModal } from '../components';
import { ProjectsTable } from '../components/projects-table/projects-table';
import { getProjectsFiltersConfig } from '../constants/project-filters';
import {
  PROJECTS_FILTER_KEY_STATUS,
  PROJECTS_TABLE_FILTER_KEYS,
  PROJECTS_TABLE_FILTERS_STORAGE_KEY,
} from '../constants/projects-filter-keys';
import { getColumns } from '../constants/projects-table-columns';
import { useProjects } from '../hooks/use-projects';
import { ProjectListItem, ProjectStatus } from '../types/project';

// All statuses are selected by default except Archived
const DEFAULT_PROJECTS_STATUS_FILTER = [ProjectStatus.Active, ProjectStatus.Tentative];

export const ProjectsPage = () => {
  const { t } = useTranslation(['authenticated.projects', 'common']);
  const navigate = useNavigate();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  usePersistedTableFilters({
    storageKey: PROJECTS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: PROJECTS_TABLE_FILTER_KEYS,
    defaultFilters: { [PROJECTS_FILTER_KEY_STATUS]: DEFAULT_PROJECTS_STATUS_FILTER },
  });

  const filterConfig = useMemo(() => getProjectsFiltersConfig(), []);
  const filterState = useTableFilters(filterConfig);
  const appliedFilters = filterState.activeFilters; // -> use appliedFilters for api request filtering
  const columns = useMemo(() => getColumns(), []);

  const { data, isLoading, isFetching, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useProjects({
      filters: appliedFilters,
    });

  const { hasPendingRefresh, isRefreshing, refresh } = useFcmListRefresh({
    notificationType: FcmNotificationType.PROJECT_UPDATED,
    queryKey: ['projects'],
    isFetching,
  });

  const { table, saveColumnSizing } = useDataTable<ProjectListItem>({
    data: data?.items || [],
    // @ts-ignore
    columns,
    getRowId: row => row.id.toString(),
    tableId: TableIds.PROJECTS,
    initialState: {
      columnPinning: { left: ['select', 'name'] },
    },
  });

  const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);

  const buildRowHref = (project: ProjectListItem) => `/projects/${project.id}`;

  const handleRowClick = (project: ProjectListItem) => {
    navigate(buildRowHref(project));
  };

  const { hasPermission } = usePermissions();
  const isCreateProjectsEnabled = hasPermission(OpsiumPolicy.ProjectsCreate);
  const isDeleteProjectsEnabled = hasPermission(OpsiumPolicy.ProjectsDelete);

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    table.resetRowSelection();
  };

  return (
    <PageTemplate
      title={t('authenticated.projects:projects')}
      useStickyHeader
      className="p-0"
      headerClassName="items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]"
      contentClassName="relative"
      rightActions={
        <div className="flex gap-2">
          {isCreateProjectsEnabled && (
            <Button size="sm" onClick={() => navigate('/projects/new')}>
              <CirclePlus />
              {t('authenticated.projects:addProject')}
            </Button>
          )}

          {/*TODO to be added in next versions*/}
          {/*<Button size="sm">
            <Download />
            {t('authenticated.projects:importProject')}
          </Button>*/}
        </div>
      }
    >
      <Separator className="ml-[-16px] w-[calc(100%+32px)]" />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4">
        {hasPendingRefresh && (
          <RefreshData
            onRefresh={refresh}
            isPending={isRefreshing}
            dataUpdatedAt={null}
            description={t('authenticated.projects:refreshBanner.description')}
            buttonTitle={t('authenticated.projects:refreshBanner.buttonTitle')}
            containerClassName="mb-4"
          />
        )}
        {/* Sticky filters section */}
        <div className="mb-4 flex shrink-0 justify-between gap-2 pt-1">
          <DataTableFiltersV2
            filtersConfig={filterConfig}
            filterState={filterState}
            tableId={TableIds.PROJECTS}
            className="flex flex-wrap gap-2"
          />
          <div className="flex flex-row items-center gap-2">
            {selectedRows.length > 0 && isDeleteProjectsEnabled && (
              <Button
                size="sm"
                variant="outlineDestructive"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                {t('common:delete')}
              </Button>
            )}
            {selectedRows.length > 0 && (
              <Button size="sm" variant="ghost" onClick={() => table.resetRowSelection()}>
                {t('common:clear')}
              </Button>
            )}
            <DataTableColumnsSelector table={table} />
          </div>
        </div>

        {/* Total count */}
        <p className="mb-4 shrink-0 text-xs text-muted-foreground">
          {t('authenticated.projects:common.allProjects')}: {data?.pagination?.total ?? 0}
        </p>

        {/* Scrollable table */}
        <ProjectsTable
          table={table}
          isLoading={isLoading || isFetchingNextPage || isRefreshing}
          hasMore={hasNextPage}
          onLoadMore={() => fetchNextPage()}
          error={error instanceof ApiError ? t(error.code) : error?.message}
          isAnyFilterApplied={Object.values(appliedFilters).some(value =>
            Array.isArray(value) ? value.length > 0 : !!value
          )}
          totalCount={data?.pagination?.total ?? 0}
          handleRowClick={handleRowClick}
          getRowHref={buildRowHref}
          fixedHeight
          tableId={TableIds.PROJECTS}
          onResizeEnd={saveColumnSizing}
        />

        {/* Modals */}
        <DeleteProjectsModal
          projectsForDelete={selectedRows}
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onSuccess={handleCloseDeleteModal}
        />
      </div>
    </PageTemplate>
  );
};
